import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../redux/store";
import { fetchTranslation } from "../../../redux/Slices/translationDataSlice";
import { ServicesService } from "../../../services/services.service";
import { CorporateService } from "../../../services/corporate.service";
import { TranslationKeys } from "../../../interface/site.interface";
import { useLangPath } from "../../../hooks/useLangPath";
import MobileAppSection from "../../../components/pages/home/mobile-app-section/MobileAppSection";
import TitleUpdater from "../../../components/shared/title/TitleUpdater";
import Spinner from "../../../components/shared/spinner/Spinner";
import styles from "./ServiceDetail.module.scss";

const CORPORATE_SLUGS = ["korporativ", "corporate", "korporativnyy"];

const SLUG_TO_TITLE_KEY: Record<string, keyof TranslationKeys> = {
  "baglamalarin-dasinmasi":  "services_packages_transportation",
  "packages-transportation": "services_packages_transportation",
  "perevozka-bagazha":       "services_packages_transportation",
  "kuryer-sifarisi":         "services_courier_order",
  "courier-order":           "services_courier_order",
  "zakaz-kuriera":           "services_courier_order",
  "poct-sifarisi":           "services_post_order",
  "post-order":              "services_post_order",
  "pochtovyy-zakaz":         "services_post_order",
  "musteri-xidmetleri":      "services_customer_services",
  "customer-services":       "services_customer_services",
  "obsluzhivanie-klientov":  "services_customer_services",
  "korporativ":              "corporate_title",
  "corporate":               "corporate_title",
  "korporativnyy":           "corporate_title",
};

const ServiceDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const { data } = useSelector((state: RootState) => state.translationData);
  const language = useSelector((state: RootState) => state.language.language);
  const dispatch = useDispatch<AppDispatch>();
  const initialRender = useRef(true);
  const lp = useLangPath();

  const [content, setContent] = useState("");
  const [loading, setLoading] = useState(false);

  const titleKey = slug ? SLUG_TO_TITLE_KEY[slug] : undefined;
  const title = titleKey ? ((data?.[titleKey] as string) || "") : "";

  useEffect(() => {
    if (initialRender.current) {
      initialRender.current = false;
      if (!data) dispatch(fetchTranslation());
    } else {
      dispatch(fetchTranslation());
    }
  }, [dispatch, language]);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);

    if (CORPORATE_SLUGS.includes(slug)) {
      CorporateService.getCorporate()
        .then((res) => setContent(res?.data?.corporate_title || ""))
        .catch(() => setContent(""))
        .finally(() => setLoading(false));
      return;
    }

    ServicesService.getServicePage(slug)
      .then((html) => setContent(html))
      .catch(() => setContent(""))
      .finally(() => setLoading(false));
  }, [slug, language]);

  return (
    <div id={styles.service_detail_page}>
      <TitleUpdater
        title={title ? `${data?.colibri || "Colibri"} | ${title}` : `${data?.colibri || "Colibri"} | ${data?.services_title || "Xidmətlər"}`}
      />
      <div className="container">
        <div className={styles.service_detail}>
          <Link to={lp("services")} className={styles.back_link}>
            ← {data?.services_title || "Xidmətlər"}
          </Link>
          <div className={styles.service_content}>
            {loading ? (
              <div className={styles.loader}>
                <Spinner size="large" />
              </div>
            ) : (
              <>
                {title && <h1 className={styles.service_title}>{title}</h1>}
                <div
                  className={styles.content}
                  dangerouslySetInnerHTML={{ __html: content }}
                />
              </>
            )}
          </div>
        </div>
      </div>
      <MobileAppSection />
    </div>
  );
};

export default ServiceDetail;
